import { useCallback, useEffect, useState } from 'react'

import { useTagContext } from '../../../contexts/tagContext'
import { usePannellumViewer } from '../../../hooks/usePannellum'
import { useImageViewerContext } from '../../views/image_viewer/imageViewerContext'
import { ViewerPosition } from '../../views/image_viewer/types'

import { CreatedRFI } from './components/CreatedRFI'
import { LinkExistingRFIForm } from './components/LinkExistingRFIForm'
import { NewRFIForm } from './components/NewRFIForm'
import { SaveImageForm } from './components/SaveImageForm'
import { ProcoreManagerContainer } from './components/styles'

interface ProcoreManagerProps {
  viewerId: string
  viewerPosition: ViewerPosition
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper return type
  hydrate: (tagsToUpdate?: number[]) => Promise<any>
  onTakeScreenshot(callback: (blob: Blob) => void): void
}

export const ProcoreManager = ({
  viewerId,
  viewerPosition,
  hydrate,
  onTakeScreenshot,
}: ProcoreManagerProps) => {
  const [transform, setTransform] = useState<string>('')
  const { state: tagState, toggleDisplay } = useTagContext()
  const { state: ImageViewerState } = useImageViewerContext()
  const { viewer, removeHotSpot } = usePannellumViewer(viewerId)
  const currentTag = tagState.tags.current

  const renderDialogue = useCallback(() => {
    const dom = document.getElementById('rfi-container')
    if (dom && viewer && currentTag) {
      const ctya = currentTag.yaw,
        canvasPitch = viewerPosition.pitch,
        canvasYaw = viewerPosition.yaw - ImageViewerState.master.data.angle,
        canvasHfov = viewerPosition.hfov,
        hsPitchSin = Math.sin((currentTag.pitch * Math.PI) / 180),
        hsPitchCos = Math.cos((currentTag.pitch * Math.PI) / 180),
        configPitchSin = Math.sin((canvasPitch * Math.PI) / 180),
        configPitchCos = Math.cos((canvasPitch * Math.PI) / 180),
        yawCos = Math.cos(((-ctya + canvasYaw) * Math.PI) / 180)
      const z =
        hsPitchSin * configPitchSin + hsPitchCos * yawCos * configPitchCos
      if (
        (ctya <= 90 && ctya > -90 && z <= 0) ||
        ((ctya > 90 || ctya <= -90) && z <= 0)
      ) {
        // eslint-disable-next-line no-console -- TODO: Remove debug console.log
        console.log('no visible')
      } else {
        const yawSin = Math.sin(((-ctya + canvasYaw) * Math.PI) / 180),
          hfovTan = Math.tan((canvasHfov * Math.PI) / 360)
        // Subpixel rendering doesn't work in Firefox
        // https://bugzilla.mozilla.org/show_bug.cgi?id=739176
        const canvas = viewer.getRenderer().getCanvas(),
          canvasWidth = canvas.clientWidth,
          canvasHeight = canvas.clientHeight
        const coord = [
          ((-canvasWidth / hfovTan) * yawSin * hsPitchCos) / z / 2,
          ((-canvasWidth / hfovTan) *
            (hsPitchSin * configPitchCos -
              hsPitchCos * yawCos * configPitchSin)) /
            z /
            2,
        ]
        // Apply transform
        coord[0] += (canvasWidth - dom.offsetWidth + 410) / 2
        coord[1] += (canvasHeight - dom.offsetHeight - 165) / 2

        const margin = 25
        const translateX = Math.min(
          Math.max(coord[0], margin),
          canvasWidth - dom.offsetWidth - margin,
        )
        const translateY = Math.min(
          Math.max(coord[1], margin),
          canvasHeight - dom.offsetHeight - margin,
        )

        const t = `translate(${translateX}px, ${translateY}px) translateZ(9999px)`
        setTransform(t)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: viewerPosition.hfov, viewerPosition.pitch, and viewerPosition.yaw intentionally omitted to prevent infinite loops
  }, [currentTag, viewer, ImageViewerState.master.data.angle])

  useEffect(() => {
    renderDialogue()
  })

  const onCloseForm = () => {
    toggleDisplay()
    removeHotSpot(currentTag.id)
  }

  return (
    <>
      {tagState.displayDialogue && (
        <ProcoreManagerContainer
          id="rfi-container"
          className="pnlm-ignore oco-dialogue"
          style={{ transform: transform }}
        >
          {currentTag.type === 'PROCORE-NEW-RFI' && (
            <NewRFIForm onCloseForm={onCloseForm} hydrate={hydrate} />
          )}
          {currentTag.type === 'PROCORE-LINK-RFI' && (
            <LinkExistingRFIForm onCloseForm={onCloseForm} hydrate={hydrate} />
          )}
          {currentTag.type === 'PROCORE-SAVE-IMAGE' && (
            <SaveImageForm
              onCloseForm={onCloseForm}
              onTakeScreenshot={onTakeScreenshot}
            />
          )}
          {currentTag.type === 'RFI' && <CreatedRFI hydrate={hydrate} />}
        </ProcoreManagerContainer>
      )}
    </>
  )
}
